import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppContext } from "../../../../AppContext";
import {
  formatAccessDateTime,
  getAccessActorLabel,
} from "../../../utilities/plannyAccessUtility";
import {
  IStudioAccessReasonFormModel,
  studioAccessReasonFormDefaultValues,
  studioAccessReasonFormSchema,
} from "../models/studioAccessReasonFormModel";

/** How long "Да, отвори" must be held to open the studio without logging (the back door). */
export const STUDIO_ACCESS_SILENT_HOLD_MS = 1500;

/**
 * Drives the global admin's "open this studio?" dialog. The gate itself (when to ask,
 * logging, switching) lives in useStudioAccessConfirmation; this shapes it for display —
 * a preview of the exact line the owner will see, the required "why am I opening it" reason,
 * and the deliberate long-press back door that opens the studio without recording.
 */
export const useStudioAccessConfirmDialog = () => {
  const {
    pendingStudioAccess,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    currentUser,
    isDesktopOrTablet,
  } = useAppContext();

  const form = useForm<IStudioAccessReasonFormModel>({
    defaultValues: studioAccessReasonFormDefaultValues,
    resolver: yupResolver(studioAccessReasonFormSchema),
  });

  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);

  const clearHold = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  // Every new question starts clean: empty reason, no pending hold.
  useEffect(() => {
    form.reset(studioAccessReasonFormDefaultValues);
    longPressFiredRef.current = false;
    clearHold();
  }, [pendingStudioAccess?.id]);

  useEffect(() => clearHold, []);

  const reason = form.watch("reason")?.trim() ?? "";

  const preview = useMemo(() => {
    if (!pendingStudioAccess) {
      return undefined;
    }

    const { date, time } = formatAccessDateTime(new Date().toISOString());

    return {
      actorLabel: getAccessActorLabel(
        `${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`,
      ),
      when: `${date}, ${time}`,
    };
  }, [pendingStudioAccess, currentUser?.firstName, currentUser?.lastName]);

  const submit = form.handleSubmit((data) => confirmStudioAccess(data.reason));

  const onConfirm = (event?: React.FormEvent | React.MouseEvent) => {
    event?.preventDefault();

    // A completed long press already opened the studio; the release's click must not also record.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }

    submit();
  };

  const onConfirmHoldStart = () => {
    if (isRecordingStudioAccess) {
      return;
    }

    longPressFiredRef.current = false;
    clearHold();
    holdTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      confirmStudioAccessSilently();
    }, STUDIO_ACCESS_SILENT_HOLD_MS);
  };

  const onConfirmHoldEnd = () => clearHold();

  return {
    form,
    isVisible: !!pendingStudioAccess,
    // Same size as the platform's other dialogs (e.g. "Избиране на обект").
    dialogWidth: isDesktopOrTablet ? "450px" : "90vw",
    studioName: pendingStudioAccess?.name ?? "",
    actorLabel: preview?.actorLabel ?? "",
    when: preview?.when ?? "",
    reasonPreview: reason ? `„${reason}“` : "",
    isRecording: isRecordingStudioAccess,
    onConfirm,
    onConfirmHoldStart,
    onConfirmHoldEnd,
    onCancel: cancelStudioAccess,
  };
};
